/**
 * Completeness gating for the OrderInProgress "Send Updates" action.
 *
 * Historically "Send Updates" was enabled purely on change-detection
 * (`hasAnyChanges`): the only completeness gate was
 * `comparatorSectionsReadyForSequencingSend`, and even that was consulted only
 * when the edit was a *sequencing* change and validated only biological
 * MOTHER/FATHER parents. That let an order be sent with a required section
 * emptied (e.g. the clinical summary erased) or an added generic comparator
 * left blank — the SPA gated on "did it change", not "is it still valid".
 *
 * These pure helpers mirror OrderStart's submit gate
 * ({@link FormCompletionChecker.isCanonicalOrderSubmittable} +
 * per-comparator {@link FormCompletionChecker.isCanonicalComparatorComplete})
 * but scoped to the sections the user actually changed on a submitted order.
 * They are deliberately decoupled from `hasSequencingChanges`: a comparator or
 * clinical-summary edit must gate on completeness whether or not the change was
 * a sequencing/tier change.
 *
 * Kept in a standalone module (not in `OrderInProgress.tsx`) so the page file
 * only exports its component (React Fast Refresh constraint) and so the gate is
 * unit-testable in isolation. The component composes the returned section ids
 * with the parent-row / guardian / clinical-invalid-char links that
 * {@link buildValidationFailureSectionLinks} already produces.
 */

import type { CanonicalOrderPayload, Comparator } from '@/ordering/canonicalOrder'
import { FormCompletionChecker } from '@/utils/FormCompletionChecker'

/**
 * Which changed section is blocking send, in the vocabulary the Send Updates
 * banner / confirm modal already use. `comparators` is intentionally coarse
 * (any incomplete comparator row — parent or otherwise); the component maps the
 * specific incomplete rows to their own fix-me links.
 */
export type IncompleteSendSectionId =
  'clinicalSummary' | 'phenotypes' | 'familyHistory' | 'comparators' | 'additionalOptions'

export interface SendUpdatesCompletenessInput {
  canonical: CanonicalOrderPayload | null | undefined
  /** Per-section dirty flags (same ones that feed `hasAnyChanges`). */
  changed: {
    clinicalSummary: boolean
    indications: boolean
    familyHistory: boolean
    /** True when any comparator row (parent OR non-parent) changed. */
    comparators: boolean
    /** True when the sequencing tier / selection changed (rides comparator adds). */
    sequencing: boolean
    /** True when the proband "Additional Options" (secondary consent) block changed. */
    patientConsent: boolean
  }
  options: {
    /** Org requires a non-empty clinical summary to submit. */
    requireClinicalSummary: boolean
    /** Org requires at least one phenotype (indications) to submit. */
    requirePhenotypes: boolean
    /** Org requires family history to submit. */
    requireFamilyHistory: boolean
    /** Incidental-findings consent is applicable for this org. */
    incidentalApplicable: boolean
    /** MRN is required on comparators (Epic/EHR flows) — optional for standalone web. */
    comparatorMrnRequired: boolean
    /** Proband opted into research contact (obligates a primary-guardian comparator's channel). */
    probandResearchContactConsent: boolean
    /** NY sample-retention consent is applicable AND authored `requireInteraction: true`. */
    nyRetentionRequireInteraction?: boolean
    /** Research-contact consent is applicable AND authored `requireInteraction: true`. */
    researchContactRequireInteraction?: boolean
  }
}

/**
 * A comparator row counts toward the send gate when it is a real comparator
 * (not the proband self-row) — every relationship, not just MOTHER/FATHER, so a
 * blank sibling / extended-relative added on a submitted order is validated too.
 */
function orderComparators(canonical: CanonicalOrderPayload | null | undefined): Comparator[] {
  const list = canonical?.comparators
  if (!Array.isArray(list)) return []
  return list.filter((c): c is Comparator => c != null && c.relationship !== 'PROBAND')
}

/**
 * Whether EVERY comparator row on the order is complete on its own terms
 * (reusing the shared OrderStart predicate). An order with no comparators is
 * vacuously complete. Exported so the component can decide per-row which links
 * to surface while sharing the same completeness definition.
 */
export function allOrderComparatorsComplete(input: SendUpdatesCompletenessInput): boolean {
  const { canonical, options } = input
  return orderComparators(canonical).every((c) =>
    FormCompletionChecker.isCanonicalComparatorComplete(c, {
      incidentalApplicable: options.incidentalApplicable,
      mrnRequired: options.comparatorMrnRequired,
      probandResearchContactConsent: options.probandResearchContactConsent,
      nyRetentionRequireInteraction: options.nyRetentionRequireInteraction,
      researchContactRequireInteraction: options.researchContactRequireInteraction,
    })
  )
}

/**
 * The set of changed sections that are currently incomplete and therefore must
 * block "Send Updates". Only sections the user actually changed are gated — an
 * unchanged (already-sent) section is never re-validated here, so this can't
 * retroactively block an order whose baseline predates a new required-field
 * rule. Erase-to-empty is handled naturally: erasing the clinical summary flips
 * `changed.clinicalSummary` true and leaves `clinicalNote` empty, so a
 * `requireClinicalSummary` org surfaces `clinicalSummary` as incomplete.
 *
 * Note: this covers required-field completeness. Free-text character validation
 * (clinical summary disallowed chars), MRN collisions, and the standalone
 * guardian research-contact requirement are gated separately by the component
 * (they are not change-scoped in the same way) and composed alongside these.
 */
export function incompleteChangedSendSections(input: SendUpdatesCompletenessInput): IncompleteSendSectionId[] {
  const { canonical, changed, options } = input
  const out: IncompleteSendSectionId[] = []
  if (!canonical) return out

  if (changed.clinicalSummary && options.requireClinicalSummary) {
    if (!canonical.clinical?.clinicalNote?.trim()) out.push('clinicalSummary')
  }

  if (changed.indications && options.requirePhenotypes) {
    const hasPhenotypes =
      Array.isArray(canonical.reason?.clinicalDiagnosis) && (canonical.reason?.clinicalDiagnosis?.length ?? 0) > 0
    if (!hasPhenotypes) out.push('phenotypes')
  }

  if (changed.familyHistory && options.requireFamilyHistory) {
    if (!canonical.family?.familyHistory?.trim()) out.push('familyHistory')
  }

  // Comparator completeness is decoupled from `hasSequencingChanges`: a
  // comparator edit (add/change of any relationship) OR a sequencing/tier change
  // (which is how a parent add rides through) must validate every comparator row.
  if ((changed.comparators || changed.sequencing) && !allOrderComparatorsComplete(input)) {
    out.push('comparators')
  }

  // Proband "Additional Options" (secondary consent): only gated when the block
  // was changed. Incidental (when applicable) needs an explicit opt-in/opt-out;
  // NY / research need an explicit true/false only when their `requireInteraction`
  // gate is engaged. Mirrors OrderStart's proband consent submit gate.
  if (changed.patientConsent && !probandConsentSendComplete(input)) {
    out.push('additionalOptions')
  }

  return out
}

/**
 * Proband secondary-consent completeness for the Send gate: incidental must be
 * an explicit opt-in/opt-out when applicable, and NY / research must be an
 * explicit true/false when their `requireInteraction` gate is engaged. Reuses
 * the shared checker so the definition matches OrderStart's submit gate.
 */
function probandConsentSendComplete(input: SendUpdatesCompletenessInput): boolean {
  const { canonical, options } = input
  if (!canonical) return true
  if (options.incidentalApplicable) {
    const sel = canonical.patient?.incidentalFindingsSelection
    if (sel !== 'opt-in' && sel !== 'opt-out') return false
  }
  return FormCompletionChecker.isCanonicalSecondaryConsentSubmittable(canonical, {
    nyRetentionRequireInteraction: options.nyRetentionRequireInteraction,
    researchContactRequireInteraction: options.researchContactRequireInteraction,
  })
}

/**
 * Convenience boolean for the button disabled state: true when at least one
 * changed section is incomplete.
 */
export function hasIncompleteChangedSendSections(input: SendUpdatesCompletenessInput): boolean {
  return incompleteChangedSendSections(input).length > 0
}
